/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember imports
import { alias } from '@ember/object/computed';
import EmberObject, { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
// External imports
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import ResponseActivityGroup from '@linc-technologies/linced-common/enums/response-activity-group';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import config from 'linced-app/config/environment';
import { restartableTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import moment from 'moment';
import { hash } from '@linc-technologies/linced-common/utils/promise';
// Internal imports
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import ThresholdLevel from 'linced-app/enums/threshold-level';
import { observes } from '@ember-decorators/object';
import {
  byKeys,
  filterBy,
  findBy,
  rejectBy,
  sortBy,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';

class ResponseActivityRow extends EmberObject {
  @alias('responseActivity.completedBy.fullName')
  completedBy;

  @alias('responseActivity.recordedBy.fullName')
  recordedBy;
}

export default class AttendanceController extends Controller {
  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  attendanceMessages;

  @service
  dates;

  @service
  fetch;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  toaster;

  Jurisdiction = Jurisdiction;
  ResponseActivityGroup = ResponseActivityGroup;
  EnrolmentStatus = EnrolmentStatus;
  ThresholdLevel = ThresholdLevel;
  Scopes = Scopes;

  @tracked
  showNotes;

  @action
  setShowNotes(value) {
    this.showNotes = value;
  }

  @tracked
  view = 'Calendar';

  @tracked
  sortDir = 'desc';

  @tracked
  sortProp = 'completedDate';

  @tracked
  filters = [];

  @tracked
  futureAbsenceError;

  @tracked
  summaryDialogOpen;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  // showMiniMaps determines whether minimaps are shown
  // if the number of periods is 1 or 2.
  // Note: `days` is a fragmentArray and `periods` is a nested fragment
  // property, so an intermediary CP keyed one level deep would go stale.
  // model-fragments does not notify the owning `days` key on nested membership
  // changes. Iterating here consumes those tags.
  get showMiniMaps() {
    let days = this.dates.currentTimetable?.days;
    if (isEmpty(days)) {
      return false;
    }
    return days.every((day) => {
      return (
        isEmpty(day.periods) ||
        day.periods.length === 1 ||
        day.periods.length === 2
      );
    });
  }

  // records is an array of all attendance records for the selected academic year
  @tracked
  records = [];

  // records by date maps period records to dates
  @computed('records.@each.date')
  get recordsByDate() {
    return this.records.reduce((byDate, record) => {
      if (isEmpty(byDate[record.date])) {
        byDate[record.date] = [];
      }
      byDate[record.date].push(record);
      return byDate;
    }, {});
  }

  // records by term maps records by term
  @tracked
  recordsByTerm = null;

  // dayCodesByTerm maps day codes by term
  @tracked
  dayCodesByTerm = null;

  // messages is an array of attendance messages for the selected term
  @tracked
  messages = [];

  // currently selected term in the attendance calendar
  @tracked
  term = null;

  // termsInSelectedYear is an array of all terms in the selected academic year
  @computed('term', 'terms.terms.[]')
  get termsInSelectedYear() {
    let academicYear = this.get('term.academicYear');
    return filterBy(this.terms.terms, 'academicYear', academicYear);
  }

  todayString = moment().format('DD/MM/YYYY');

  @alias('terms.currentTerm')
  currentTerm;

  @computed('currentTerm')
  get previousTerm() {
    let currentTerm = this.currentTerm;
    if (!currentTerm || !currentTerm.previous) {
      return null;
    }

    return {
      start: this.get('currentTerm.previous.start'),
      end: this.get('currentTerm.previous.end'),
    };
  }

  @computed('learnerAbsencesByTerm', 'currentTerm')
  get currentTermAbsences() {
    return this.learnerAbsencesByTerm[this.currentTerm.term];
  }

  @computed('model.termlyAbsences.[]')
  get learnerAbsencesByTerm() {
    let termlyAbsences = this.model.termlyAbsences || [];
    let termAbsences = { ytd: 0 };

    if (isEmpty(termlyAbsences)) {
      return termAbsences;
    }

    termlyAbsences.forEach((termEntry = {}) => {
      let term = termEntry.term;
      if (!term) {
        return;
      }

      let absentWholeDays = termEntry.absentWholeDays;
      if (absentWholeDays == null) {
        return;
      }

      termAbsences[term] = absentWholeDays;
      termAbsences.ytd += absentWholeDays;
    });

    return termAbsences;
  }

  @computed('model.responseActivities.[]', 'sortProp', 'sortDir')
  get responseActivityData() {
    return this.model.responseActivities.map((responseActivity) => {
      let detail =
        responseActivity.detail ||
        ResponseActivityGroup.getLabel(responseActivity.responseActivity);
      let completedDate = moment(
        responseActivity.completedDate,
        'YYYY-MM-DD',
      ).toDate();
      return ResponseActivityRow.create({
        id: responseActivity.id,
        responseActivity,
        completedDate,
        detail,
        lastModified: this.getLastModifiedDate(responseActivity),
        notes: responseActivity.notes,
      });
    });
  }

  @computed('sortProp', 'sortDir', 'responseActivityData')
  get sortedResponseActivityData() {
    let sorted = this.responseActivityData;

    // Sorting by a Person property.
    sorted = sortBy(sorted, this.sortProp);

    // Reverse the array if descending sort was requested.
    if (this.sortDir === 'desc') {
      sorted = sorted.reverse();
    }
    return sorted;
  }

  // filteredResponseActivityData applies the user's filters to the data array computed above.
  @computed('sortedResponseActivityData.[]', 'filters.@each.{field,value}')
  get filteredResponseActivityData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.sortedResponseActivityData;
    }

    // Map raw filters into what we need to actually perform the filtering.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      return {
        key,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.sortedResponseActivityData.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('model.person.{fullName,currentYearLevel,homeroom}')
  get exportTitle() {
    if (!isEmpty(this.model.person.homeroom)) {
      return `Response Activity Summary <br>${this.model.person.fullName} - Year: ${this.model.person.currentYearLevel} - ${this.model.person.homeroom.name}`;
    }
    return `Response Activity Summary <br>${this.model.person.fullName} - Year: ${this.model.person.currentYearLevel}`;
  }

  @computed('model.currentTermThresholds.[]')
  get currentThreshold() {
    let thresholds = this.model.currentTermThresholds;
    if (isEmpty(thresholds)) {
      return null;
    }

    // Grab the latest threshold
    let threshold = thresholds
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .at(-1);

    return threshold ? threshold.threshold : null;
  }

  @computed('model.previousTermThresholds.[]')
  get previousThreshold() {
    let thresholds = this.model.previousTermThresholds;
    if (isEmpty(thresholds)) {
      return null;
    }

    // Grab the latest threshold
    let threshold = thresholds
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .at(-1);

    return threshold ? threshold.threshold : null;
  }

  getLastModifiedDate(responseActivity) {
    let timestamp = responseActivity.updateTime || responseActivity.createTime;
    return moment.unix(timestamp).toDate();
  }

  @observes('model.person.id', 'term', 'showMiniMaps')
  _updateRecords() {
    this.updateRecords();
  }

  @action
  updateRecords() {
    if (
      isEmpty(this.model) ||
      isEmpty(this.model.person) ||
      isEmpty(this.term) ||
      isEmpty(this.termsInSelectedYear)
    ) {
      return;
    }
    // Query params
    let personId = this.get('model.person.id');
    let dateFrom = this.get('term.start');
    let dateTo = this.get('term.end');

    if (this.showMiniMaps) {
      // We are showing the minimaps
      // Load the attendance records for each term in the year and map them
      if (this.jurisdiction === Jurisdiction.AU_NSW) {
        // Add a timeout to combat race condition
        // (nz-moe-days are sometimes retrieved before the backend is done updating)
        setTimeout(() => {
          hash(
            this.termsInSelectedYear.reduce((terms, term) => {
              terms[term.term] = this.store.query('attendance/nz-moe-day', {
                personId,
                dateFrom: term.start,
                dateTo: term.end,
              });
              return terms;
            }, {}),
          ).then((dayCodesByTerm) => {
            this.dayCodesByTerm = dayCodesByTerm;
          });
        }, 1000);

        // Load current term's attendance records for main calendar
        this.store
          .query('attendance/record', { personId, dateFrom, dateTo })
          .then((records) => {
            this.records = records;
          });
      } else {
        hash(
          this.termsInSelectedYear.reduce((terms, term) => {
            terms[term.term] = this.store.query('attendance/record', {
              personId,
              dateFrom: term.start,
              dateTo: term.end,
            });
            return terms;
          }, {}),
        ).then((recordsByTerm) => {
          this.records = get(recordsByTerm, this.get('term.term'));
          this.recordsByTerm = recordsByTerm;
        });
      }
    } else {
      // Not showing the minimaps
      // Load the currently selected term
      this.store
        .query('attendance/record', { personId, dateFrom, dateTo })
        .then((records) => {
          this.records = records;
        });
    }

    // Load the messages only for the selected term
    // to use in the selected term's attendance calendar
    this.attendanceMessages
      .queryRange(personId, dateFrom, dateTo)
      .then((messages) => {
        this.messages = messages;
      });
  }

  // todayTask loads the person's attendance records for today. loadToday
  // performs it, and updateToday performs it again after a save.
  todayTask = restartableTask(async () => {
    let personId = this.get('model.person.id');
    let dateId = this.get('dates.today.id');
    let records = dateId
      ? await this.store.query('attendance/record', { personId, dateId })
      : [];
    return { personId, dateId, records };
  });

  // loadToday performs todayTask on the first render, and again when the
  // person or the date changes (staff open another learner, or midnight).
  loadToday = modifier(() => {
    this.getProperties('model.person.id', 'dates.today.id');
    this.todayTask.perform();
  });

  // todayLoaded is true when todayTask has the records of the person and the
  // date on the page. Until then, the widgets do not show, so staff cannot
  // mark a record of another learner or date.
  get todayLoaded() {
    let result = this.todayTask.lastSuccessful?.value;
    return (
      result !== undefined &&
      result.personId === this.get('model.person.id') &&
      result.dateId === this.get('dates.today.id')
    );
  }

  // today gives the records that todayTask loaded, or [] until they load.
  get today() {
    return this.todayLoaded ? this.todayTask.lastSuccessful.value.records : [];
  }

  @action
  updateToday() {
    this.todayTask.perform();
  }

  @alias('model.person.learner.currentSiblings')
  siblings;

  @tracked
  futureAbsenceDialogOpen = false;

  @tracked
  responseActivityDeleteDialogOpen = false;

  @tracked
  responseActivityToDelete = null;

  @tracked
  futureAbsenceSaving = false;

  @tracked
  futureAbsencePeriodsToMark = 'all';

  @action
  setFutureAbsencePeriodsToMark(value) {
    this.futureAbsencePeriodsToMark = value;
  }

  @action
  setSummaryAcademicYear(value) {
    this.summaryAcademicYear = value;
  }

  @tracked
  futureAbsencePeriods = [];

  @tracked
  futureAbsenceStartDate = '';

  @action
  setFutureAbsenceStartDate(value) {
    this.futureAbsenceStartDate = value;
  }

  @tracked
  futureAbsenceStartPeriod = null;

  @action
  setFutureAbsenceStartPeriod(value) {
    this.futureAbsenceStartPeriod = value;
  }

  @tracked
  futureAbsenceEndDate = '';

  @action
  setFutureAbsenceEndDate(value) {
    this.futureAbsenceEndDate = value;
  }

  @tracked
  futureAbsenceEndPeriod = null;

  @action
  setFutureAbsenceEndPeriod(value) {
    this.futureAbsenceEndPeriod = value;
  }

  @tracked
  futureAbsenceCode = null;

  @action
  setFutureAbsenceCode(value) {
    this.futureAbsenceCode = value;
  }

  @tracked
  futureAbsenceNotes = '';

  @action
  setFutureAbsenceNotes(value) {
    this.futureAbsenceNotes = value;
  }

  @tracked
  futureAbsenceSiblings = [];

  futureAbsenceMinDate = moment().format('YYYY-MM-DD');

  @computed('attendanceCodes.futureCodes.[]')
  get futureAbsenceCodes() {
    let codes = this.attendanceCodes.futureCodes.slice();
    codes.push({
      code: '',
      label: 'Clear mark',
    });
    return rejectBy(codes, 'old');
  }

  @computed(
    'dates.structures.[]',
    'futureAbsenceStartDate',
    'futureAbsenceEndDate',
  )
  get futureAbsenceStructures() {
    let structures = [];
    if (
      isEmpty(this.futureAbsenceStartDate) ||
      isEmpty(this.futureAbsenceEndDate)
    ) {
      return structures;
    }

    structures.push(this.dates.structureForDate(this.futureAbsenceStartDate));

    if (this.futureAbsenceStartDate !== this.futureAbsenceEndDate) {
      // If the attendance spans multiple days, check for additional timetable structures.
      let extraStructures = this.dates.structures
        .filter((structure) => {
          let isAfterCurrent =
            structure.get('effectiveFrom') > this.futureAbsenceStartDate;
          let isBeforeEnd =
            structure.get('effectiveFrom') <= this.futureAbsenceEndDate;
          return isAfterCurrent && isBeforeEnd;
        })
        .sort(byKeys('effectiveFrom', 'createTime'));

      if (extraStructures.length) {
        structures = [...structures, ...extraStructures];
      }
    }

    return structures;
  }

  get futureAbsenceStartPeriods() {
    if (isEmpty(this.futureAbsenceStartDate)) {
      return [];
    }
    let day = this.dates.dayForDate(this.futureAbsenceStartDate);
    if (isEmpty(day)) {
      return [];
    }
    return day.periods;
  }

  get futureAbsenceEndPeriods() {
    if (isEmpty(this.futureAbsenceEndDate)) {
      return [];
    }
    let day = this.dates.dayForDate(this.futureAbsenceEndDate);
    if (isEmpty(day)) {
      return [];
    }
    return day.periods;
  }

  @computed
  get startDateValidation() {
    return [
      {
        message: 'Future absence cannot start in the past',
        validate: (val) => {
          let parsed = moment(val);
          let now = moment();
          return parsed.isValid() && parsed.isSameOrAfter(now, 'd');
        },
      },
      {
        message: 'Future absence must start on a weekday',
        validate: (val) => {
          let parsed = moment(val);
          return parsed.isoWeekday() < 6;
        },
      },
    ];
  }

  get endDateValidation() {
    return [
      {
        message: 'Future Absence cannot end before it starts',
        validate: (val) => {
          let parsed = moment(val);
          let start = moment(this.futureAbsenceStartDate);
          return (
            parsed.isValid() &&
            start.isValid() &&
            parsed.isSameOrAfter(start, 'd')
          );
        },
      },
      {
        message: 'Future absence must end on a weekday',
        validate: (val) => {
          let parsed = moment(val);
          return parsed.isoWeekday() < 6;
        },
      },
    ];
  }

  @observes('futureAbsenceStartDate')
  _resetStartPeriod() {
    let parsed = moment(this.futureAbsenceStartDate);
    if (parsed.isValid()) {
      let day = this.dates.dayForDate(this.futureAbsenceStartDate);
      if (!isEmpty(day) && !isEmpty(day.periods) && day.periods.length > 0) {
        this.futureAbsenceStartPeriod = day.periods[0];
      }
    }
  }

  @observes('futureAbsenceEndDate')
  _resetEndPeriod() {
    let parsed = moment(this.futureAbsenceEndDate);
    if (parsed.isValid()) {
      let day = this.dates.dayForDate(this.futureAbsenceEndDate);
      if (!isEmpty(day) && !isEmpty(day.periods) && day.periods.length > 0) {
        this.futureAbsenceEndPeriod = day.periods.at(-1);
      }
    }
  }

  @tracked summaryAcademicYear = '';

  @computed('summaryAcademicYearObjects', 'summaryAcademicYear')
  get summaryAcademicYearObject() {
    if (this.summaryAcademicYear) {
      return this.summaryAcademicYearObjects.find(
        (item) => item.label === this.summaryAcademicYear,
      );
    }
    // Default to first object here
    return this.summaryAcademicYearObjects[0];
  }

  @computed('summaryAcademicYearObjects')
  get summaryAcademicYears() {
    return this.summaryAcademicYearObjects.map((summaryAcademicYearObject) => {
      return summaryAcademicYearObject.label;
    });
  }

  @computed('model.enrolments.@each.{entryDate,exitDate}')
  get summaryAcademicYearObjects() {
    let allAcademicYears = this.model.enrolments.reduce(
      (academicYears, enrolment) => {
        let entryDate = isEmpty(enrolment.entryDate)
          ? moment()
          : moment(enrolment.entryDate, 'YYYY-MM-DD');
        let exitDate = isEmpty(enrolment.exitDate)
          ? moment()
          : moment(enrolment.exitDate, 'YYYY-MM-DD');
        for (let date = entryDate; date <= exitDate; date.add(1, 'year')) {
          academicYears.push(this.academicYearObjectAtDate(date));
        }
        // Ensure that we include the academic year in which the exitDate falls
        // so that we don't miss it.
        academicYears.push(this.academicYearObjectAtDate(exitDate));
        return academicYears;
      },
      [],
    );
    return uniqBy(allAcademicYears, 'label').sort(byKeys('label')).reverse();
  }

  // TODO: Pop this into the dates service in common if/when it starts getting shared around
  academicYearObjectAtDate(date) {
    let dateYear = date.year();
    let rolloverMonthDate = this.dates.rolloverMonthDate;
    if (
      date.isBefore(moment(`${dateYear}-${rolloverMonthDate}`, 'YYYY-MM-DD'))
    ) {
      dateYear -= 1;
    }
    return {
      label:
        rolloverMonthDate === '01-01'
          ? `${dateYear}`
          : `${dateYear}-${dateYear + 1}`,
      start: `${dateYear}-${rolloverMonthDate}`,
      end: moment(`${dateYear + 1}-${rolloverMonthDate}`, 'YYYY-MM-DD')
        .subtract(1, 'day')
        .format('YYYY-MM-DD'),
    };
  }

  @alias('model.enrolments')
  enrolments;

  @computed('dates.currentDate', 'messages')
  get todaysMessages() {
    return filterBy(this.messages, 'attendanceDate', this.dates.currentDate);
  }

  init() {
    super.init(...arguments);
    this.set('term', this.terms.currentOrNextTerm);
  }

  @action
  findRecord(date, periodId, record) {
    return record.get('date') === date && record.get('period.id') === periodId;
  }

  @action
  isEnrolledOnDate(date, enrolments) {
    let checkDate = moment(date, 'YYYY-MM-DD');
    let today = moment();

    return enrolments.some((enrolment) => {
      if (
        enrolment.status === EnrolmentStatus.CURRENT &&
        today.isSame(checkDate, 'day')
      ) {
        // Student has a current enrolment, and we're checking for today.
        return true;
      }

      let entryDate = moment(enrolment.entryDate, 'YYYY-MM-DD');
      let exitDate = moment(enrolment.exitDate, 'YYYY-MM-DD');

      if (entryDate.isValid() && exitDate.isValid()) {
        // We have start and end dates - checkDate must be between them (inclusive).
        return checkDate.isBetween(entryDate, exitDate, 'day', '[]');
      }
      if (entryDate.isValid()) {
        // No end date, but we have a start date.  Make sure they've started.
        return checkDate.isSameOrAfter(entryDate, 'day');
      }
      if (exitDate.isValid()) {
        // No start date, but we have an end date.  Make sure they haven't finished.
        return checkDate.isSameOrBefore(exitDate, 'day');
      }

      // No valid start or end date, and it's not today.  We can't tell whether they were here.
      return false;
    });
  }

  @action
  openFutureAbsenceDialog() {
    let periods = (this.get('dates.currentTimetableDay.periods') ?? []).slice();
    let currentDate = moment().format('YYYY-MM-DD');
    this.setProperties({
      futureAbsenceDialogOpen: true,
      futureAbsenceSaving: false,
      futureAbsencePeriodsToMark: 'all',
      futureAbsencePeriods: [],
      futureAbsenceStartDate: currentDate,
      futureAbsenceStartPeriod: periods[0],
      futureAbsenceEndDate: currentDate,
      futureAbsenceEndPeriod: periods.at(-1),
      futureAbsenceCode: null,
      futureAbsenceNotes: '',
      futureAbsenceSiblings: [],
      futureAbsenceMinDate: currentDate,
    });
  }

  @action
  toggleFutureAbsencePeriod(period, checked) {
    if (checked) {
      this.futureAbsencePeriods = [...this.futureAbsencePeriods, period];
    } else {
      this.futureAbsencePeriods = this.futureAbsencePeriods.filter(
        (p) => p !== period,
      );
    }
  }

  @action
  toggleFutureAbsenceSibling(sibling, checked) {
    if (checked) {
      this.futureAbsenceSiblings = [...this.futureAbsenceSiblings, sibling];
    } else {
      this.futureAbsenceSiblings = this.futureAbsenceSiblings.filter(
        (s) => s !== sibling,
      );
    }
  }

  @action
  closeFutureAbsenceDialog() {
    this.futureAbsenceDialogOpen = false;
  }

  @action
  saveFutureAbsence() {
    this.futureAbsenceSaving = true;
    return this._ensureAttendanceDataLoadedForRange(
      this.futureAbsenceStartDate,
      this.futureAbsenceEndDate,
    )
      .then(() => {
        return this._persistFutureAbsence();
      })
      .catch(() => {
        this.futureAbsenceSaving = false;
        this.toaster.error(
          'Unable to load attendance records for the selected future absence dates. ' +
            'Please refresh the page and try again.',
        );
        return Promise.resolve();
      });
  }

  @action
  openSummaryDialog() {
    this.summaryDialogOpen = true;
  }

  @action
  closeSummaryDialog() {
    this.summaryDialogOpen = false;
  }

  @action
  getDateCrossed() {
    let thresholds = this.model.currentTermThresholds;
    if (isEmpty(thresholds)) {
      return null;
    }

    let sortedThresholds = thresholds
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .reverse();
    let prevThreshold = null;
    let dateCrossed = null;

    for (let threshold of sortedThresholds) {
      if (isEmpty(prevThreshold)) {
        prevThreshold = threshold;
        dateCrossed = threshold.date;
        continue;
      }
      if (threshold.threshold !== prevThreshold.threshold) {
        dateCrossed = prevThreshold.date;
        return moment(dateCrossed).format('DD/MM/YYYY');
      }
      prevThreshold = threshold;
      dateCrossed = threshold.date;
    }
    return moment(dateCrossed).format('DD/MM/YYYY');
  }

  @action
  generateAbsentCodeSummary(records) {
    let summaryMap = {};
    let absentCodes = [];

    records.forEach((record) => {
      if (
        this.attendanceCodes.truancyUCodes.some(
          (code) => code.code === record.code,
        ) ||
        this.attendanceCodes.truancyJCodes.some(
          (code) => code.code === record.code,
        )
      ) {
        absentCodes.push(record.code);
      }
    });

    absentCodes.forEach((code) => {
      if (!summaryMap[code]) {
        summaryMap[code] = 0;
      }
      summaryMap[code]++;
    });

    let entries = Object.entries(summaryMap).map(([code, count]) => {
      let percent = Math.floor((count / absentCodes.length) * 100);
      return `${code} - ${percent}%`;
    });

    return entries.join(' | ');
  }

  @action
  deleteResponseActivity() {
    this.responseActivityToDelete
      .destroyRecord()
      .then(() => {
        this.toaster.success('Response Activity deleted successfully!');
        this.model.responseActivities.update();
      })
      .catch(() => {
        this.toaster.error('Response Activity delete failed!');
      })
      .finally(() => {
        this.responseActivityDeleteDialogOpen = false;
        this.responseActivityToDelete = null;
      });
  }

  @action
  openDeleteResponseActivityDialog(responseActivity) {
    this.responseActivityDeleteDialogOpen = true;
    this.responseActivityToDelete = responseActivity;
  }

  @action
  updateResponseActivities() {
    this.model.responseActivities.update();
  }

  @action
  toggleView(view) {
    this.view = view === 'AMP' ? 'Calendar' : 'AMP';
  }

  @action
  changeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  copyNoteToFirstPeriod(note, dayString, timetableDay) {
    this.copyNoteToPeriods(note, dayString, timetableDay, 'first');
  }

  @action
  copyNoteToAllPeriods(note, dayString, timetableDay) {
    this.copyNoteToPeriods(note, dayString, timetableDay, 'all');
  }

  _persistFutureAbsence() {
    let markAll = this.futureAbsencePeriodsToMark === 'all';
    let recordDate = moment(this.futureAbsenceStartDate, 'YYYY-MM-DD');
    let endDate = moment(this.futureAbsenceEndDate, 'YYYY-MM-DD');
    let day = this.dates.dayForDate(this.futureAbsenceStartDate);
    let siblingHomerooms = {};
    if (isEmpty(day)) {
      // No valid timetable day - can't process this.
      this.futureAbsenceError = true;
      this.futureAbsenceSaving = false;
      return Promise.resolve();
    }
    let periodIndex = day.periods.indexOf(this.futureAbsenceStartPeriod);
    if (periodIndex < 0) {
      // Period not selected/found - use the first period of the day.
      periodIndex = 0;
    }

    // Get any existing records within the selected range, so that we can update them were necessary.
    let params = {
      personId: this.model.person.id,
      dateFrom: this.futureAbsenceStartDate,
      dateTo: this.futureAbsenceEndDate,
    };
    let queries = {
      [this.model.person.id]: this.store.query('attendance/record', params),
    };
    if (this.futureAbsenceSiblings.length) {
      for (let i = 0; i < this.futureAbsenceSiblings.length; i++) {
        let siblingParams = JSON.parse(JSON.stringify(params));
        let siblingId = this.futureAbsenceSiblings[i];
        let sibling = this.store.peekRecord('person', siblingId);
        siblingParams.personId = siblingId;
        siblingHomerooms[siblingId] = isEmpty(sibling)
          ? ''
          : sibling.get('homeroom.id');
        queries[siblingId] = this.store.query(
          'attendance/record',
          siblingParams,
        );
      }
    }

    return hash(queries).then((existingRecords) => {
      let newRecords = [];
      let updatedRecords = [];
      let deleteRecords = [];
      let homeroomId = this.model.person.get('homeroom.id');
      while (recordDate.isSameOrBefore(endDate, 'd')) {
        let formattedDate = recordDate.format('YYYY-MM-DD');
        day = this.dates.dayForDate(formattedDate);
        let dateObj = this.dates.getDate(formattedDate);
        let schoolClosed =
          isEmpty(dateObj) ||
          isEmpty(dateObj.status) ||
          dateObj.status === SchoolStatus.CLOSED;
        if (isEmpty(dateObj) || isEmpty(day) || schoolClosed) {
          // Date not found in calendar - too far in the future?
          // Or day not found in schedule - is a weekend?
          // Or school is closed on this day (i.e. holidays).
          recordDate.add(1, 'd');
          periodIndex = 0;
          continue;
        }
        let endIndex = day.periods.length - 1;
        if (recordDate.isSame(endDate, 'd')) {
          endIndex = day.periods.indexOf(this.futureAbsenceEndPeriod);
          if (endIndex === -1) {
            // If no valid end period selected, assume whole day.
            endIndex = day.periods.length - 1;
          }
        }

        while (periodIndex <= endIndex) {
          let period = day.periods[periodIndex];
          if (markAll || this.futureAbsencePeriods.includes(period)) {
            // Marking all periods within range, or this period matches the custom selection.
            let newRecord = {
              schoolId: this.session.activeSchool,
              dateId: dateObj.id,
              date: formattedDate,
              periodId: period.id,
              personId: this.model.person.id,
              authorId: this.session.person.id,
              code: this.futureAbsenceCode.code,
              notes: this.futureAbsenceNotes,
              homeroomId,
            };

            if (this.trackMovements) {
              let effectiveFrom = moment(
                `${formattedDate}T${period.startTime}`,
                'YYYY-MM-DDTHH:mm',
              ).unix();
              newRecord.effectiveFrom = effectiveFrom;
              newRecord.movements = [
                {
                  time: effectiveFrom,
                  code: newRecord.code,
                  authorId: newRecord.authorId,
                  notes: newRecord.notes,
                },
              ];
            }

            let existing = this.findFutureRecord(
              existingRecords[this.model.person.id],
              dateObj.id,
              period.id,
            );

            if (isEmpty(existing)) {
              // Don't create new records if we're clearing existing marks.
              if (this.futureAbsenceCode.code !== '') {
                // Add attendance record for this period to the list to bulk create.
                newRecords.push(newRecord);
              }
            } else {
              let record = JSON.parse(JSON.stringify(newRecord));
              record.id = existing.id;
              record.reason = 'Future Absence';
              if (this.futureAbsenceCode.code !== '') {
                updatedRecords.push(record);
              } else {
                existing.set('auditReason', 'Future Absence');
                deleteRecords.push(existing);
              }
            }

            // If any siblings were selected, also push records for them.
            if (this.futureAbsenceSiblings.length) {
              for (let i = 0; i < this.futureAbsenceSiblings.length; i++) {
                let siblingRecord = JSON.parse(JSON.stringify(newRecord));
                siblingRecord.personId = this.futureAbsenceSiblings[i];
                siblingRecord.homeroomId =
                  siblingHomerooms[siblingRecord.personId];

                let existing = this.findFutureRecord(
                  existingRecords[siblingRecord.personId],
                  dateObj.id,
                  period.id,
                );

                if (isEmpty(existing)) {
                  // Don't create new records if we're clearing existing marks.
                  if (this.futureAbsenceCode.code !== '') {
                    // Add attendance record for this period to the list to bulk create.
                    newRecords.push(siblingRecord);
                  }
                } else {
                  siblingRecord.id = existing.id;
                  siblingRecord.reason = 'Future Absence';
                  if (this.futureAbsenceCode.code !== '') {
                    updatedRecords.push(siblingRecord);
                  } else {
                    existing.set('auditReason', 'Future Absence');
                    deleteRecords.push(existing);
                  }
                }
              }
            }
          }
          periodIndex++;
        }

        recordDate.add(1, 'd');
        periodIndex = 0;
      }

      if (
        newRecords.length > 0 ||
        updatedRecords.length > 0 ||
        deleteRecords.length > 0
      ) {
        // Bulk create/update attendance records, via fetch service.
        let promises = [];

        if (newRecords.length > 0) {
          let options = {
            data: JSON.stringify({ records: newRecords }),
          };

          promises.push(
            this.fetch.post(
              `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulk`,
              options,
            ),
          );
        }

        if (updatedRecords.length > 0) {
          let options = {
            data: JSON.stringify({ records: updatedRecords }),
          };

          promises.push(
            this.fetch.post(
              `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulkupdate`,
              options,
            ),
          );
        }

        if (deleteRecords.length > 0) {
          deleteRecords.forEach((record) => {
            promises.push(record.destroyRecord());
          });
        }

        return Promise.all(promises).then(() => {
          this.updateRecords();
          this.updateToday();

          this.futureAbsenceSaving = false;
          this.futureAbsenceDialogOpen = false;

          this.toaster.success(`Future Attendance saved successfully.`);
        });
      }

      // No records to create.  Just close the dialog and be done with it - but do so on the next runloop,
      // as we've already touched `futureAbsenceSaving` at the start of this function.
      next(this, function () {
        this.futureAbsenceSaving = false;
        this.futureAbsenceDialogOpen = false;

        this.toaster.error(
          `Failed saving the Future Attendance due to no timetable periods. ` +
            `Please check the School dates calendar to ensure the selected dates are within a term and try again.`,
        );
      });
      return Promise.resolve();
    });
  }

  _ensureAttendanceDataLoadedForRange(startDate, endDate) {
    let start = moment(startDate, 'YYYY-MM-DD', true);
    if (!start.isValid()) {
      return Promise.resolve();
    }
    let end = moment(endDate, 'YYYY-MM-DD', true);
    if (!end.isValid()) {
      end = start.clone();
    }

    let currentYear = moment().year();
    let yearsToLoad = [];

    if (start.year() !== currentYear) {
      yearsToLoad.push(start.year());
    }
    if (end.year() !== currentYear && end.year() !== start.year()) {
      yearsToLoad.push(end.year());
    }

    if (!yearsToLoad.length) {
      return Promise.resolve();
    }

    return Promise.all(yearsToLoad.map((year) => this.dates.loadYear(year)));
  }

  copyNoteToPeriods(note, dayString, timetableDay, mode) {
    if (!note) {
      return;
    }
    if (!dayString) {
      dayString = this.dates.todayDate;
    }
    if (isEmpty(timetableDay)) {
      timetableDay = this.dates.currentTimetableDay;
    }
    let promises = [];

    let periods = timetableDay.periods
      .slice()
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

    switch (mode) {
      case 'first':
        promises.push(this.updateOrCreateRecord(periods[0], dayString, note));
        break;

      case 'all':
      default:
        periods.forEach((period) => {
          promises.push(this.updateOrCreateRecord(period, dayString, note));
        });
        break;
    }
    Promise.all(promises)
      .then(() => {
        this.toaster.success('Attendance notes saved.');
        this.updateRecords();
        this.updateToday();
      })
      .catch(() => {
        this.toaster.error('Error saving attendance notes!');
      });
  }

  updateOrCreateRecord(period, dayString, note) {
    let dayRecords = this.recordsByDate[dayString] || [];
    // Find if records for today already exist using the period
    let thisPeriodsRecord = findBy(dayRecords, 'period_id', period.id);
    if (isEmpty(thisPeriodsRecord)) {
      let newRecord = this.attendance.createAttendanceRecord(
        this.model.person,
        dayString,
        period,
        { code: '' },
        note,
      );
      return newRecord.save();
    }
    if (note === thisPeriodsRecord.get('notes')) {
      return Promise.resolve(null);
    }
    let updatedRecord = this.attendance.updateAttendanceRecord(
      thisPeriodsRecord.get('code'),
      note,
      'Added note',
      thisPeriodsRecord,
    );
    return updatedRecord.save();
  }

  findFutureRecord(records, dateId, periodId) {
    return records.find((record) => {
      return record.date_id === dateId && record.period_id === periodId;
    });
  }
}
